import React, { useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import aspectRatios, { AspectRatioIcon } from '../../../constants/aspectRatios';
import resolutionMap, { RESOLUTION_OPTIONS } from '../../../constants/resolutionMap';
import AngleViewDropdown from '../../../components/AngleViewDropdown';
import RoomTypeDropdown from '../../../components/RoomTypeDropdown';
import ModelSelectionDropdown from '../../../components/ModelSelectionDropdown';
import { isFluxModel } from '../../../services/fluxService';

const CADSettings = ({
  selectedRoomType,
  setSelectedRoomType,
  commonAngleView,
  setCommonAngleView,
  commonAngleRef,
  commonAngleGlowStyle,
  angleViewRef,
  angleViewGlowStyle,
  roomTypeRef,
  roomTypeGlowStyle,
  modelSelectionRef,
  modelSelectionGlowStyle,
  aspectRatioGlowRef,
  aspectRatioGlowStyle,
  resolutionGlowRef,
  resolutionGlowStyle,
  promptGlowRef,
  promptGlowStyle,
  selectedAngleView,
  setSelectedAngleView,
  selectedModel,
  setSelectedModel,
  selectedImageSize,
  setSelectedImageSize,
  isAspectRatioOpen,
  setIsAspectRatioOpen,
  selectedResolution,
  setSelectedResolution,
  isResolutionOpen,
  setIsResolutionOpen,
  prompt,
  setPrompt,
  onPromptChange
}) => {
  const aspectRatioRef = useRef(null);
  const resolutionRef = useRef(null);
  const promptRef = useRef(null);
  const MAX_PROMPT_LENGTH = 1600;

  // Available data arrays
  // Models constant removed - using shared ModelSelectionDropdown component
  const roomTypes = [
    '--', 'Full Room CAD', 'Bedroom', 'Living room', 'Meeting room', 'Bathroom', 'Cabinet',
    'Kitchen', 'Reception', 'Lounge', 'Bar', 'Terrace',
    'Co-working area', 'Restaurant'
  ];


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (aspectRatioRef.current && aspectRatioRef.current.offsetParent !== null && !aspectRatioRef.current.contains(event.target)) {
        setIsAspectRatioOpen(false);
      }
      if (resolutionRef.current && resolutionRef.current.offsetParent !== null && !resolutionRef.current.contains(event.target)) {
        setIsResolutionOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsAspectRatioOpen, setIsResolutionOpen]);

  // Get dimensions for a specific resolution key
  const getResolutionDimensions = (resKey) => {
    const ratioMap = resolutionMap[selectedImageSize];
    if (ratioMap && ratioMap[resKey]) {
      return ratioMap[resKey];
    }
    return resolutionMap['1:1'][resKey] || { width: 1024, height: 1024 };
  };

  return (
    <div className="glassmorphic-card rounded-3xl p-6 space-y-4">
      {/* Common Angle Views + Room Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Common Angle Views Toggle */}
        <div ref={commonAngleRef} className="rounded-xl" style={commonAngleGlowStyle}>
          <label className="text-white/70 text-xs mb-2 block">Common angle views</label>
          <div className="flex items-center justify-start gap-2 py-3">
            <span className={`text-xs transition-colors ${commonAngleView === 'frontal' ? 'text-white' : 'text-white/50'}`}>
              Frontal view
            </span>
            <button
              onClick={() => {
                if (commonAngleView === 'frontal') {
                  setCommonAngleView('topdown');
                  setSelectedAngleView('Top-Down Flat View (90°)');
                } else {
                  setCommonAngleView('frontal');
                  setSelectedAngleView('Straight-On Eye-Level View');
                }
              }}
              className={`relative w-14 h-7 rounded-full transition-all duration-300 ${commonAngleView ? 'bg-orange-500' : 'bg-white/20'}`}>
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${commonAngleView === 'frontal' ? 'left-0.5' : 'right-0.5'}`}></div>
            </button>
            <span className={`text-xs transition-colors ${commonAngleView === 'topdown' ? 'text-white' : 'text-white/50'}`}>
              Top-down view
            </span>
          </div>
        </div>
        {/* Room Section */}
        <div ref={roomTypeRef} className="rounded-xl" style={roomTypeGlowStyle}>
          <RoomTypeDropdown
            value={selectedRoomType}
            onChange={(val) => {
              setSelectedRoomType(val);
              if (val === 'Custom') {
                setTimeout(() => {
                  if (promptRef?.current) {
                    promptRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    promptRef.current.focus();
                  }
                }, 80);
              }
            }}
            label="Room section"
            extraTopOptions={['Full Floor Plan']}
            showCustomOption
          />
        </div>
      </div>

      {/* Additional Angle View */}
      <div ref={angleViewRef} className="rounded-xl" style={angleViewGlowStyle}>
      <AngleViewDropdown value={selectedAngleView} onChange={(val) => {
        setSelectedAngleView(val);
        if (val === 'Straight-On Eye-Level View') {
          setCommonAngleView('frontal');
        } else if (val === 'Top-Down Flat View (90°)') {
          setCommonAngleView('topdown');
        } else {
          setCommonAngleView(null);
        }
      }} />
      </div>

      {/* Model Selection and Aspect Ratio */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div ref={modelSelectionRef} className="rounded-xl" style={modelSelectionGlowStyle}>
          <ModelSelectionDropdown
            value={selectedModel}
            onChange={setSelectedModel}
          />
        </div>

        <div ref={aspectRatioGlowRef} className="rounded-xl" style={aspectRatioGlowStyle}>
        <div ref={aspectRatioRef}>
          <label className="text-white/70 text-xs mb-2 block">
            Aspect Ratio
          </label>
          <div className="relative">
            {/* Dropdown trigger */}
            <button
              onClick={() =>
                setIsAspectRatioOpen(!isAspectRatioOpen)
              }
              className="w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all"
            >
              <div className="flex items-center gap-3">
                <AspectRatioIcon
                  ratio={selectedImageSize}
                  size={18}
                  className="text-orange-500"
                />
                <span className="text-white text-sm">
                  {selectedImageSize}
                </span>
              </div>
              <Icon
                name={
                  isAspectRatioOpen ? "ChevronUp" : "ChevronDown"
                }
                size={16}
                className="text-white/50"
              />
            </button>
            {/* Dropdown menu */}
            {isAspectRatioOpen && (
              <div
                className="absolute z-10 w-full bottom-full mb-2 rounded-2xl border border-white/20 shadow-2xl bg-black overflow-hidden"
                style={{
                  backgroundColor: "rgb(0, 0, 0)",
                  backdropFilter: "none"
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {aspectRatios?.map((ratio) => (
                  <button
                    key={ratio?.value}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Button clicked:', ratio?.value);
                      setSelectedImageSize(ratio?.value);
                      setIsAspectRatioOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 cursor-pointer ${
                      selectedImageSize === ratio?.value
                        ? "bg-orange-500/20 hover:bg-orange-500/30"
                        : "hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <AspectRatioIcon
                        ratio={ratio?.value}
                        size={18}
                        className={
                          selectedImageSize === ratio?.value
                            ? "text-orange-500"
                            : "text-white/50"
                        }
                      />
                      <span
                        className={`text-sm ${
                          selectedImageSize === ratio?.value
                            ? "text-white font-medium"
                            : "text-white/70"
                        }`}
                      >
                        {ratio?.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Resolution Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div ref={resolutionGlowRef} className="rounded-xl" style={resolutionGlowStyle}>
        <div ref={resolutionRef}>
          {!isFluxModel(selectedModel) && (
          <>
          <label className="text-white/70 text-xs mb-2 block">
            Resolution
          </label>
          <div className="relative">
            <button
              onClick={() => setIsResolutionOpen(!isResolutionOpen)}
              className={`w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}
            >
              <span className="text-white text-sm font-medium">{selectedResolution}</span>
              <div className="flex items-center gap-2">
                <span className="text-white/50 text-xs">
                    {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                  </span>
                <Icon
                  name={isResolutionOpen ? "ChevronUp" : "ChevronDown"}
                  size={16}
                  className="text-white/50"
                />
              </div>
            </button>
            {isResolutionOpen && (
              <div
                className="absolute z-10 w-full bottom-full mb-2 rounded-2xl border border-white/20 shadow-2xl bg-black overflow-hidden"
                style={{
                  backgroundColor: "rgb(0, 0, 0)",
                  backdropFilter: "none"
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {RESOLUTION_OPTIONS.map((resKey) => {
                  const dims = getResolutionDimensions(resKey);
                  return (
                    <button
                      key={resKey}
                      type="button"
                      onClick={() => {
                        setSelectedResolution(resKey);
                        setIsResolutionOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 cursor-pointer ${
                        selectedResolution === resKey
                          ? "bg-orange-500/20 hover:bg-orange-500/30"
                          : "hover:bg-white/5"
                      }`}
                    >
                      <span
                        className={`text-sm ${
                          selectedResolution === resKey
                            ? "text-white font-medium"
                            : "text-white/70"
                        }`}
                      >
                        {resKey}
                      </span>
                      <span
                          className={`text-xs ${
                            selectedResolution === resKey
                              ? "text-orange-400"
                              : "text-white/50"
                          }`}
                        >
                          {dims.width}×{dims.height}
                        </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          </>
          )}
        </div>
        </div>
      </div>

      {/* Prompt */}
      <div ref={promptGlowRef} className="rounded-xl" style={promptGlowStyle}>
        <label className="text-white/70 text-xs mb-2 block">
          Prompt: ({prompt.length}/{MAX_PROMPT_LENGTH})
        </label>
        {selectedRoomType === 'Custom' && (
          <p className="text-orange-400/80 text-[11px] mb-2 flex items-center gap-1">✏️ Describe the room type you want in the prompt below</p>
        )}
        <textarea
          ref={promptRef}
          placeholder={selectedRoomType === 'Custom' ? 'Describe the room type (e.g. yoga studio, reading nook, wine cellar...)' : 'Additional request (optional)'}
          rows="5"
          value={prompt}
          onChange={onPromptChange}
          className={`w-full glassmorphic-input rounded-2xl px-4 py-3 text-white bg-white/5 focus:outline-none transition-all placeholder:text-white/30 resize-none overflow-y-auto min-h-[120px] max-h-[300px] ${selectedRoomType === 'Custom' ? 'border-2 border-orange-500/60 focus:border-orange-500' : 'border border-white/10 focus:border-orange-500/50'}`}
        />
      </div>
    </div>
  );
};

export default CADSettings;